import { Box, DialogContent, DialogTitle } from "@mui/material";
import { DynamicForm } from "commons/ultilities/dynamic-form/components/dynamic-form";
import { useForm } from "commons/ultilities/dynamic-form/hooks/use-form";
import dayjs from "dayjs";
import { observer } from "mobx-react-lite";
import { useStores } from "mobx-stores/_root-store";
import { useCallback, useEffect } from "react";

import { workOrderPoBusiness } from "api/bussiness/work-order-po/work-order-po-bussiness";
import { dateTimeToUTCString } from "commons/ultilities/date/dayjs";
import { IUpdateWorkOrderPo } from "mobx-stores/_models/work-order-po/work-order-po";
import { DialogForm } from "share-components/templates/form/dialog-form";
import { useFormValueChanged } from "../hooks/use-on-form-value-changed";
import { getEditWorkOrderPoFormSchema } from "../schema";
import { mapUpdateWorkOrderPoModelToUpdateApiRequest } from "../ultilities/map";
import { roundTo } from "commons/ultilities";

export const EditWorkOrderPo = observer(() => {
  const {
    workOrderPoManagement: {
      openEdit,
      getWorkOrderPoDetail,
      setProp,
      workOrderPoDetail,
    },
  } = useStores();
  const {
    form: { controls, isValid },
    getControl,
    setDefaults,
    submitHandler,
  } = useForm<IUpdateWorkOrderPo>(
    (form) => getEditWorkOrderPoFormSchema(form),
    {
      id: "",
      date: dateTimeToUTCString(dayjs()),
      woNumber: "",
      customerId: "",
      note: "",
      purchaseOrderId: "",
      dealingNameReadOnlyInfo: "",
      productCodeReadOnlyInfo: "",
      productNameReadOnlyInfo: "",
      productBarcodeReadOnlyInfo: "",
      orderDateReadOnlyInfo: "",
      deliveryDateReadOnlyInfo: "",
      orderAmountReadOnlyInfo: "",
      orderAmountUpperReadOnlyInfo: "",
      orderAmountLowerReadOnlyInfo: "",
      paperTypeReadOnlyInfo: "",
      gsmReadOnlyInfo: "",
      paperBrandReadOnlyInfo: "",
      firstFullPaperLengthReadOnlyInfo: "",
      firstFullPaperSizeReadOnlyInfo: "",
      secondFullPaperLengthReadOnlyInfo: "",
      secondFullPaperSizeReadOnlyInfo: "",
      firstPrintPaperLengthReadOnlyInfo: "",
      firstPrintPaperSizeReadOnlyInfo: "",
      secondPrintPaperLengthReadOnlyInfo: "",
      secondPrintPaperSizeReadOnlyInfo: "",
      purchaseOrderItemId: "",
      woBatchNumber: "",
      poItemProductionAmount: null,
      productionAmount: null,
      totalPaperWeight: null,
      poItemFirstFullPaperAmount: null,
      poItemSecondFullPaperAmount: null,
      poItemFirstPrintPaperAmount: null,
      poItemSecondPrintPaperAmount: null,
      poItemFirstFullPaperLength: null,
      poItemFirstFullPaperSize: null,
      poItemSecondFullPaperLength: null,
      poItemSecondFullPaperSize: null,
      poItemPrintPaperLength: null,
      poItemPrintPaperSize: null,
      poItemSecondPrintPaperLength: null,
      poItemSecondPrintPaperSize: null,
      poItemGSM: null,
      orderNumber: null,
    },
  );

  const { onValueChanged } = useFormValueChanged({ getControl });

  useEffect(() => {
    if (workOrderPoDetail) {
      setDefaults({
        id: workOrderPoDetail.id,
        date: workOrderPoDetail.date,
        woNumber: workOrderPoDetail.woNumber,
        customerId: workOrderPoDetail.customerId,
        note: workOrderPoDetail.note,
        purchaseOrderId: workOrderPoDetail.purchaseOrderId,
        dealingNameReadOnlyInfo: workOrderPoDetail.customerDisplay?.dealingName || "",
        productCodeReadOnlyInfo:
          workOrderPoDetail.purchaseOrderItem?.product?.code || "",
        productNameReadOnlyInfo:
          workOrderPoDetail.purchaseOrderItem?.product?.name || "",
        productBarcodeReadOnlyInfo:
          workOrderPoDetail.purchaseOrderItem?.product?.barcode || "",
        orderDateReadOnlyInfo: workOrderPoDetail.purchaseOrder?.poDate || "",
        deliveryDateReadOnlyInfo:
          workOrderPoDetail.purchaseOrderItem?.deliveryDate || "",
        orderAmountReadOnlyInfo:
          workOrderPoDetail.purchaseOrderItem?.quantityStandard?.toString() ||
          "",
        orderAmountUpperReadOnlyInfo:
          workOrderPoDetail.purchaseOrderItem?.quantityUpper?.toString() || "",
        orderAmountLowerReadOnlyInfo:
          workOrderPoDetail.purchaseOrderItem?.quantityLower?.toString() || "",
        paperTypeReadOnlyInfo:
          workOrderPoDetail.purchaseOrderItem?.product?.material?.paperType ||
          "",
        gsmReadOnlyInfo:
          workOrderPoDetail.purchaseOrderItem?.product?.material?.gsmStandard?.toString() ||
          "",
        paperBrandReadOnlyInfo:
          workOrderPoDetail.purchaseOrderItem?.product?.material?.paperBrand ||
          "",
        firstFullPaperLengthReadOnlyInfo:
          workOrderPoDetail.purchaseOrderItem?.product?.process?.firstFullPaperLength?.toString() ||
          "",
        firstFullPaperSizeReadOnlyInfo:
          workOrderPoDetail.purchaseOrderItem?.product?.process?.firstFullPaperSize?.toString() ||
          "",
        secondFullPaperLengthReadOnlyInfo:
          workOrderPoDetail.purchaseOrderItem?.product?.process?.secondFullPaperLength?.toString() ||
          "",
        secondFullPaperSizeReadOnlyInfo:
          workOrderPoDetail.purchaseOrderItem?.product?.process?.secondFullPaperSize?.toString() ||
          "",
        firstPrintPaperLengthReadOnlyInfo:
          workOrderPoDetail.purchaseOrderItem?.product?.process?.printPaperLength?.toString() ||
          "",
        firstPrintPaperSizeReadOnlyInfo:
          workOrderPoDetail.purchaseOrderItem?.product?.process?.printPaperSize?.toString() ||
          "",
        secondPrintPaperLengthReadOnlyInfo:
          workOrderPoDetail.purchaseOrderItem?.product?.process?.secondPrintPaperLength?.toString() ||
          "",
        secondPrintPaperSizeReadOnlyInfo:
          workOrderPoDetail.purchaseOrderItem?.product?.process?.secondPrintPaperSize?.toString() ||
          "",
        purchaseOrderItemId: workOrderPoDetail.purchaseOrderItemId,
        woBatchNumber: workOrderPoDetail.woBatchNumber,
        poItemProductionAmount: workOrderPoDetail.poItemProductionAmount,
        productionAmount: workOrderPoDetail.productionAmount,
        totalPaperWeight: roundTo(workOrderPoDetail?.totalPaperWeight || 0, 2),
        poItemFirstFullPaperAmount:
          workOrderPoDetail.poItemFirstFullPaperAmount,
        poItemSecondFullPaperAmount:
          workOrderPoDetail.poItemSecondFullPaperAmount,
        poItemFirstPrintPaperAmount:
          workOrderPoDetail.poItemFirstPrintPaperAmount,
        poItemSecondPrintPaperAmount:
          workOrderPoDetail.poItemSecondPrintPaperAmount,
        poItemFirstFullPaperLength:
          workOrderPoDetail.poItemFirstFullPaperLength,
        poItemFirstFullPaperSize: workOrderPoDetail.poItemFirstFullPaperSize,
        poItemSecondFullPaperLength:
          workOrderPoDetail.poItemSecondFullPaperLength,
        poItemSecondFullPaperSize: workOrderPoDetail.poItemSecondFullPaperSize,
        poItemPrintPaperLength: workOrderPoDetail.poItemPrintPaperLength,
        poItemPrintPaperSize: workOrderPoDetail.poItemPrintPaperSize,
        poItemSecondPrintPaperLength:
          workOrderPoDetail.poItemSecondPrintPaperLength,
        poItemSecondPrintPaperSize:
          workOrderPoDetail.poItemSecondPrintPaperSize,
        poItemGSM: workOrderPoDetail.poItemGSM,
        lossRate: workOrderPoDetail.lossRate,
        completedAmount: workOrderPoDetail.completedAmount,
        poItemNote: workOrderPoDetail.poItemNote,
        orderNumber: workOrderPoDetail.orderNumber,
        statusId: workOrderPoDetail.statusId,
      });
    }
  }, [workOrderPoDetail, setDefaults]);

  useEffect(() => {
    const purchaseOrderSelection = getControl("purchaseOrderId");
    purchaseOrderSelection.setProp("optionItems", [
      {
        label: workOrderPoDetail?.purchaseOrder?.poNumber || "",
        value: workOrderPoDetail?.purchaseOrder?.id,
      },
    ]);

    const purchaseOrderItemSelection = getControl("purchaseOrderItemId");
    purchaseOrderItemSelection.setProp(
      "optionItems",
      workOrderPoDetail?.purchaseOrder?.items?.map((x) => ({
        value: x.id,
        label: x.product?.name ?? "",
      })) ?? [],
    );
  }, [
    getControl,
    workOrderPoDetail?.purchaseOrder?.id,
    workOrderPoDetail?.purchaseOrder?.poNumber,
    workOrderPoDetail?.purchaseOrder?.items,
  ]);

  const saveHandler = useCallback(
    async (data: IUpdateWorkOrderPo) => {
      try {
        if (!workOrderPoDetail?.id) return;
        const req = mapUpdateWorkOrderPoModelToUpdateApiRequest(data);
        await workOrderPoBusiness.update(workOrderPoDetail?.id, req);
        await getWorkOrderPoDetail(workOrderPoDetail?.id);
        setProp("openEdit", false);
      } catch (ex) {
        console.error(ex);
      }
    },
    [getWorkOrderPoDetail, workOrderPoDetail?.id, setProp],
  );

  return (
    <DialogForm
      open={openEdit}
      fullWidth
      maxWidth={"md"}
      onClose={() => setProp("openEdit", false)}
      onCancelBtnClicked={() => setProp("openEdit", false)}
      onSaveBtnClick={async () => await submitHandler(saveHandler)}
      saveBtnProps={{
        disabled: !isValid,
      }}
    >
      <DialogTitle>Sửa lệnh sản xuất</DialogTitle>
      <DialogContent>
        <Box sx={{ marginTop: "10px" }}>
          <DynamicForm
            controls={controls}
            onValueChanged={onValueChanged}
          ></DynamicForm>
        </Box>
      </DialogContent>
    </DialogForm>
  );
});
